import { applyLocale, isLocaleHolder, languageOf } from '../i18n/applyLocale'
import { groupAttributes } from '../utils/groupAttributes'

/**
 * Keeps vue-i18n on the language of the group the page is built for, on both
 * sides.
 *
 * The i18n module boots on English and only its browser detection moves it,
 * which runs in the browser and reads a per visitor cookie. A page rendered
 * once per group has to take its language from the group instead, and the
 * hydrating browser has to agree with the HTML it received. Without this a
 * French visitor got English strings from the server and French from the
 * browser, thirty six hydration mismatches on one page.
 *
 * The language comes straight from the group: the request context the gate
 * filled on the server, the group cookie in the browser. Not from LocaleStore,
 * which is not meant to be created this early. Without a group there is
 * nothing to do, and the module behaves as it does today.
 *
 * Set once, at setup, and nowhere else. Registering an app:created hook from
 * here, even an empty one, made the news feed handler lose the Nuxt instance
 * mid render and the page came back with no articles. The module's own
 * detection agrees with the group's language anyway, since the cookie was
 * written from the same browser settings it reads.
 */
export default defineNuxtPlugin({
  name: 'i18n-locale',
  // The module's plugin provides $i18n, so this one has to come after it.
  dependsOn: ['i18n:plugin'],
  setup(nuxtApp) {
    const i18n: unknown = nuxtApp.$i18n
    if (!isLocaleHolder(i18n)) return
    const language = languageOf(groupAttributes()?.locale)
    if (!language) return
    applyLocale(i18n, language)
  },
})
